import * as React from "react";
import { useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { STATE_REPORT_ACTIVE_FILTERS } from "../constants/MobilityStoreRecoil";
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { addFilter, getFilterValue, renderMultiSelect } from "../../../utils/FilterUtils";
import { ID_FILTER_LEGAL_ENTITIES, ID_FILTER_DEALERS, ID_FILTER_REPORT_CREATIONDATEFROM, ID_FILTER_REPORT_CREATIONDATETO, ID_FILTER_SEARCH, msReportDefaultValues } from "../constants/MobilityStoreFilters";
import { asEndOfDay, asStartOfDay } from "../../../utils/FinanceDateUtils";
import AdvancedFilter from "../../../component/AdvancedFilter";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";

function ReportsFilter() {
    const { t } = useTranslation("finance", { keyPrefix: "mobility-store" });
    const { t: tg } = useTranslation("translation");

    const setFilterDefs = useSetRecoilState(STATE_REPORT_ACTIVE_FILTERS);
    const filterDefs = useRecoilValue(STATE_REPORT_ACTIVE_FILTERS);
    const [search, setSearch] = useState("");

    const {
        globalData: { user },
    } = useContext(GlobalContext);

    const legalEntityOptions: any[] = user.legalEntities
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tg(`legalEntities.${x.name}`),
        }));

    const dealerOptions: any[] = user.dealers
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tg(`dealers.${x.name}`),
        }));
    
    const [dealers, setDealers] = useState<any[]>(dealerOptions);


    const schema = Yup.object().shape({
        reportDateFrom: Yup.date().nullable().test("testFrom", t('filters.errors.invalid-date'), (value, context) => {
            return !value || !context.parent.reportDateTo || (value <= context.parent.reportDateTo);
        }).typeError(t('filters.errors.invalid-date')),
        reportDateTo: Yup.date().nullable().test("testTo", t('filters.errors.invalid-date'), (value, context) => {
            return !value || !context.parent.reportDateFrom || (value >= context.parent.reportDateFrom);
        }).typeError(t('filters.errors.invalid-date')),
        dealers: Yup.array(),
        legalEntities: Yup.array().min(1, tg('validation.selectAtLeastOne'))
    });

    const defaultValues = {
        reportDateFrom: getFilterValue(ID_FILTER_REPORT_CREATIONDATEFROM, filterDefs) || null,
        reportDateTo: getFilterValue(ID_FILTER_REPORT_CREATIONDATETO, filterDefs) || null,
        legalEntities: getFilterValue(ID_FILTER_LEGAL_ENTITIES, filterDefs) || legalEntityOptions.map((x: any) => x.value),
        dealers: getFilterValue(ID_FILTER_DEALERS, filterDefs) || [],
    }


    const renderReportDateFromField = (field: any, error: any, state: any, values: any) => (
            <DatePicker
                    views={['day']}
                    label={t('filters.reports.' + ID_FILTER_REPORT_CREATIONDATEFROM)}
                    value={field.value}
                    maxDate={values['reportDateTo'] !== null ? values['reportDateTo'] : null}
                    onChange={field.onChange}
                    renderInput={(params) => <TextField {...params} style={{ width: "100%" }}
                                                        error={error !== undefined}
                                                        helperText={error ? error.message : ""}
                                                        id={"reportDateFrom"}
                    />}
                    inputFormat={"L"}
            />
    );

    const renderReportDateToField = (field: any, error: any, state: any, values: any) => (
            <DatePicker
                    views={['day']}
                    label={t('filters.reports.' + ID_FILTER_REPORT_CREATIONDATETO)}
                    value={field.value}
                    minDate={values['reportDateFrom'] !== null ? values['reportDateFrom'] : null}
                    onChange={field.onChange}
                    renderInput={(params) => <TextField {...params} style={{ width: "100%" }}
                                                        error={error !== undefined}
                                                        helperText={error ? error.message : ""}
                                                        id={"reportDateTo"}
                    />}
                    inputFormat={"L"}
            />
    );

    const fields = {
        reportDateFrom: renderReportDateFromField,
        reportDateTo: renderReportDateToField,
        legalEntities: renderMultiSelect(
                      tg("shared.legalEntities"),
                      legalEntityOptions,
                      "legalEntities",
                      legalEntityOptions.length <= 1,
                      (value: any, setValue: any) => {
                        setValue('dealers', []);
                        let dealers = user?.dealers
                          ?.filter(
                            (x: any) =>
                              value?.length > 0 &&
                              x.legalEntities.some((e: any) => value.includes(e.name)) &&
                              x.name.toLowerCase() !== "all"
                          )
                          .map((x: any) => {
                            return {
                              label: tg(`dealers.${x.name}`),
                              value: x.name,
                            };
                          });
                        setDealers(dealers);
                      }
                    ),
                    dealers: renderMultiSelect(tg("shared.dealers"), dealers, "dealers", dealerOptions.length <= 1),
    }

    const submit = (data: any) => {
        addFilter(setFilterDefs, ID_FILTER_SEARCH, search);
        addFilter(setFilterDefs, ID_FILTER_REPORT_CREATIONDATEFROM, asStartOfDay(data.reportDateFrom));
        addFilter(setFilterDefs, ID_FILTER_REPORT_CREATIONDATETO, asEndOfDay(data.reportDateTo));
        addFilter(setFilterDefs, ID_FILTER_LEGAL_ENTITIES, data.legalEntities);
        addFilter(setFilterDefs, ID_FILTER_DEALERS, data.dealers);
    }

    const reset = () => {
        setFilterDefs([]);
    }

    return (
            <AdvancedFilter schema={schema} fields={fields} defaultValues={defaultValues}
                            searchInput={search} setSearchInput={setSearch}
                            onSubmit={submit}
                            onReset={reset}
                            customDefaultValues={msReportDefaultValues}
            />
    )

}

export default ReportsFilter;